import { databaseRef } from 'config/firebase'
import { get } from 'lodash'
import FormType, {
  BaseFormAssessments,
  formTypeRefs,
  formTypes
} from '../data/questionnaires/FormType'
import IForm from '../data/questionnaires/IForm'
import IQuestionAnswer, {
  IGainedAnswers
} from '../data/questionnaires/IQuestionAnswer'
import IScore from '../data/questionnaires/IScore'
import motorTypes from '../data/questionnaires/Types/Motor/MotorTypes'
import SocialCategories from '../data/questionnaires/Types/Social/SocialCategories'
import { getCurrentAssessment } from './patient'
import { getAge, getFirstKey, getPercentile } from './utils'

export const formKeyByType = (type: FormType) => {
  switch (type) {
    case FormType.social:
      return 'social_forms'
    case FormType.grossMotor:
      return 'gross_motor_forms'
    case FormType.fineMotor:
      return 'fine_motor_forms'
    case FormType.selfEsteem:
      return 'self_esteem_forms'
    case FormType.language:
      return 'language_forms'

    default:
      return ''
  }
}

export const getFormByTypeAndChild = async (
  type: any,
  child: any,
  options: {
    storage?: object
    Lib?: any
    nextId?: string
    startAge?: number
    attempt?: number
  } = {}
) => {
  options.attempt = options.attempt === undefined ? 1 : options.attempt

  if (!child || !type || options.attempt > 2) {
    return null
  }

  const formType = formKeyByType(type)

  let forms: IForm[] = options.storage && options.storage[formType]

  if (!forms) {
    forms = (
      await databaseRef
        .child('questionnaires')
        .child(formType)
        .once('value')
    ).val()
    if (!forms || !Array.isArray(forms)) {
      return null
    }
    options.Lib?.add(forms, formType)
  }

  if (options.nextId) {
    return forms.find(form => form.id === options.nextId)
  }

  const lockedDate = child.assessments
    ? child.currentAssessment
    : new Date().getTime()

  const age = getAge(child.birth_date, lockedDate)

  return (
    forms.filter(
      form =>
        form.type === type &&
        form.start_age <= age &&
        (form.end_age < 0 || age < form.end_age)
    )[0] || null
  )
}

export const getFormByTypeAndId = async (type: FormType, id: string) => {
  const forms: IForm[] = (
    await databaseRef
      .child('questionnaires')
      .child(formKeyByType(type))
      .once('value')
  ).val()

  if (!forms || !Array.isArray(forms)) {
    return null
  }

  return forms.find(form => form.id === id) || null
}

export const getTempAnswers = async (
  form: IForm,
  parentId: string,
  childId: string,
  relevantId: string
) => {
  if (!form || !parentId || !childId || !relevantId) {
    return { answers: [], gainedAnswers: [] }
  }
  const answersRecord: { answers: object; key: string } = (
    await databaseRef
      .child('answers_tmp')
      .orderByChild('key')
      .equalTo(`${form.type}|${parentId}|${childId}|${relevantId}|${form.id}`)
      .once('value')
  ).val()

  const key = getFirstKey(answersRecord)

  return (
    (answersRecord &&
      answersRecord[key] && {
        answers: answersRecord[key].answers,
        gainedAnswers: answersRecord[key].gainedAnswers
      }) || { answers: [], gainedAnswers: [] }
  )
}

export const saveScores = async (
  score: IScore,
  form: IForm,
  child: any,
  sideAnswers?: IGainedAnswers[]
) => {
  const relevantAssessment = child.currentAssessment
  if (!relevantAssessment) {
    return false
  }
  score.form_id = form.id
  const sideScores =
    sideAnswers && sideAnswers.map(a => getScore(a.answers, a.form))

  // tslint:disable-next-line: no-unused-expression
  sideAnswers &&
    sideAnswers.forEach(s => {
      if (s) {
        const { answers } = s
        score.categories[s.form.id] = answers.reduce(
          (acc, ans, i) =>
            ans.answer
              ? {
                  ...acc,
                  [s.form.questions[i].id]: s.form.questions[i].italic
                    ? ans.answer.italic_points
                    : ans.answer.points
                }
              : acc,

          {}
        )
      }
    })

  // tslint:disable-next-line: no-unused-expression
  sideScores &&
    sideScores.forEach(s => {
      if (s && s.form_id !== undefined) {
        score.total_results[s.form_id] = s.total_results.total_score
      }
    })

  await databaseRef
    .child('children')
    .child(child.patientId)
    .child('assessments')
    .child(relevantAssessment)
    .child(formTypeRefs[form.type].replace('.', '/'))
    .set(score)
  await updateScores(form.type, child)
  return true
}

const updateScores = async (formType: FormType, child: any) => {
  const relevantAssessment = child.currentAssessment
  if (!relevantAssessment) {
    return
  }

  const childRef = databaseRef.child('children').child(child.patientId)

  const assessmentRef = childRef.child('assessments').child(relevantAssessment)
  const assessments = (await assessmentRef.once('value')).val()

  switch (formType) {
    case FormType.grossMotor:
    case FormType.fineMotor:
      updateMotor(assessments)
  }

  await assessmentRef.update(assessments)
}

const updateMotor = (assessments: any) => {
  const motorScores = motorTypes.map(
    type =>
      assessments[BaseFormAssessments.motor][type] &&
      assessments[BaseFormAssessments.motor][type].total_results.total_score
        .score
  )
  if (motorScores.every(s => s !== undefined)) {
    assessments[BaseFormAssessments.motor].total_results = {}
    assessments[BaseFormAssessments.motor].total_results.total_score = {}
    assessments[BaseFormAssessments.motor].total_results.total_score.score =
      motorScores.reduce((acc, v) => acc + v) / motorScores.length
  }
}

// const updateAbilities = (ability: BaseFormAssessments, child: any) => {
//   if (!child.abilities) {
//     child.abilities = {}
//   }
//   const assessment = getCurrentAssessment(child)
//   child.abilities[ability] =
//     get(assessment, [
//       ability,
//       'total_results',
//       'total_score',
//       'score'
//     ]) || 0
// }

export const saveTempAnswers = async (
  form: IForm,
  parentId: string,
  childId: string,
  relevantId: string,
  answers: IQuestionAnswer[],
  gainedAnswers: IGainedAnswers[] = []
) => {
  if (
    !form ||
    !parentId ||
    !childId ||
    !relevantId ||
    !answers ||
    !answers.length
  ) {
    return
  }

  const answersRecord = answers.reduce(
    (acc, ans) =>
      ans.answer
        ? {
            ...acc,
            [ans.question.id]: form.answers.indexOf(ans.answer)
          }
        : acc,
    {}
  )

  const gainedAnswersRecord = await Promise.all(
    gainedAnswers.map(async a => {
      const forma = await getFormByTypeAndId(a.form.type, a.form.id)
      return {
        ...a,
        answers: a.answers.reduce(
          (acc, ans) =>
            ans.answer
              ? {
                  ...acc,
                  [ans.question.id]:
                    forma &&
                    forma.answers.findIndex(
                      b => b.answer === ans.answer!.answer
                    )
                }
              : acc,
          {}
        )
      }
    })
  )

  if (!Object.keys(answersRecord).length && !gainedAnswersRecord.length) {
    return
  }

  const storedRecord = await databaseRef
    .child('answers_tmp')
    .orderByChild('key')
    .equalTo(`${form.type}|${parentId}|${childId}|${relevantId}|${form.id}`)
    .once('value')

  if (storedRecord.exists()) {
    const key = getFirstKey(storedRecord.val())
    databaseRef
      .child('answers_tmp')
      .child(key || '')
      .update({ answers: answersRecord, gainedAnswers })
    return key
  } else {
    const record = {
      key: `${form.type}|${parentId}|${childId}|${relevantId}|${form.id}`,
      formType: form.type,
      parentId,
      childId,
      relevantId,
      answers: answersRecord,
      gainedAnswers: gainedAnswersRecord
    }
    const { key } = await databaseRef.child('answers_tmp').push(record)
    return key
  }
}

export const deleteTempAnswers = async (
  form: IForm,
  parentId: string,
  childId: string,
  relevantId: string
) => {
  if (!form || !parentId || !childId || !relevantId) {
    return
  }
  await databaseRef
    .child('answers_tmp')
    .orderByChild('key')
    .equalTo(`${form.type}|${parentId}|${childId}|${relevantId}|${form.id}`)
    .ref.remove()
}

const setFormTotalScores = (
  formType: FormType,
  score: IScore,
  options: {
    attempt?: number
    penalty?: number
  } = { attempt: 1, penalty: 0.75 }
) => {
  options.attempt = options.attempt === undefined ? 1 : options.attempt
  options.penalty = options.penalty === undefined ? 0.75 : options.penalty

  const { attempt, penalty } = options
  switch (formType) {
    case FormType.social:
      return setTotalScore(
        score,
        [
          SocialCategories.conduct_problems,
          SocialCategories.emotional_symptoms,
          SocialCategories.hyperactivity,
          SocialCategories.peer_problems
        ],
        [SocialCategories.prosocial]
      )
    case FormType.grossMotor:
    case FormType.fineMotor:
      return setTotalScore(score, [], [], Math.pow(penalty, attempt - 1))
    case FormType.selfEsteem:
      return setTotalScore(score, [], [])
    case FormType.language:
      return setTotalScore(score, [], [])
    default:
      return null
  }
}

const getValueTransform = (formType: FormType): ((v: number) => number) => {
  switch (formType) {
    default:
      return value => value
  }
}

export const getScore = (
  answers: IQuestionAnswer[],
  form: IForm,
  options: {
    attempt?: number
    penalty?: number
  } = {}
): IScore | null => {
  options.attempt = options.attempt === undefined ? 1 : options.attempt
  options.penalty = options.penalty === undefined ? 0.75 : options.penalty

  const score: IScore = {
    categories: {},
    total_results: {
      total_score: { points: 0, maxPoints: 0 }
    },
    form_id: form.id
  }
  const maxAnswerScore = Math.max(...form.answers.map(a => a.points))
  const maxItalicAnswerScore = Math.max(
    ...form.answers.map(a => a.italic_points)
  )

  const transform = getValueTransform(form.type)

  answers.forEach(({ question, answer }) => {
    if (!answer) {
      return
    }
    const value = question.italic ? answer.italic_points : answer.points

    const maxValue = question.italic ? maxItalicAnswerScore : maxAnswerScore

    const category = question.category || ''

    if (Array.isArray(category)) {
      category.forEach(curcategory => {
        if (!score.categories[curcategory]) {
          score.categories[curcategory] = {}
        }
        if (!score.total_results[curcategory]) {
          score.total_results[curcategory] = { points: 0, maxPoints: 0 }
        }
        score.categories[curcategory][question.id] = value
        score.total_results[curcategory].points += transform(value)
        score.total_results[curcategory].maxPoints += transform(maxValue)
      })
    } else {
      if (!score.categories[category]) {
        score.categories[category] = {}
      }

      if (!score.total_results[category]) {
        score.total_results[category] = { points: 0, maxPoints: 0 }
      }

      score.categories[category][question.id] = value

      score.total_results[category].points += transform(value)
      score.total_results[category].maxPoints += transform(maxValue)
    }
  })

  setFormTotalScores(form.type, score, options)

  delete score.categories['']
  delete score.total_results['']

  return score
}

export const getUncompletedFormTypes = (child: any, isPlusUser: boolean) => {
  const relevantAssessment = child.currentAssessment

  const assessments = child.assessments && child.assessments[relevantAssessment]
  if (!assessments) {
    if (!relevantAssessment) {
      return []
    } else {
      return formTypes
    }
  }
  const types = childForms(child).filter(
    type => !get(assessments, formTypeRefs[type])
  )
  if (!isPlusUser) {
    return types.includes(FormType.selfEsteem) ? [FormType.selfEsteem] : []
  }
  return types
}

export const isFormPassed = (form: IForm, score: IScore): boolean => {
  if (!form || !score) {
    return false
  }
  switch (form.type) {
    case FormType.social:
      return isSocialFormPassed(score)
    case FormType.grossMotor:
      return isGrossMotorFormPassed(score)
    case FormType.fineMotor:
      return isFineMotorFormPassed(score)
    case FormType.selfEsteem:
      return isSelfEsteemFormPassed(score)
    case FormType.language:
      return isLanguageFormPassed(score)
    default:
      return true
  }
}

const isSocialFormPassed = (score: IScore) => {
  return true
}

const isGrossMotorFormPassed = (score: IScore) => {
  const { points: s, maxPoints: ms } = score.total_results.total_score
  return s / ms > 0.75
  // temp fix should be added back after thw questionarie issue has been fixed.
  // return true;
}

const isFineMotorFormPassed = (score: IScore) => {
  const { points: s, maxPoints: ms } = score.total_results.total_score
  return s / ms > 0.75
  // temp fix should be added back after thw questionarie issue has been fixed.
  // return true;
}

const isSelfEsteemFormPassed = (score: IScore) => {
  return true
}

const isLanguageFormPassed = (score: IScore) => {
  return true
}

const setTotalScore = (
  score: IScore,
  inverted: string[],
  notIncluded: string[],
  coef: number = 1
  // isMotor: boolean = false
) => {
  const categories = Object.keys(score.categories)

  score.total_results.total_score = {
    points: 0,
    maxPoints: 0,
    score: 0
  }

  categories.forEach(c => {
    const value = getPercentile(
      score.total_results[c].points,
      score.total_results[c].maxPoints
    )
    score.total_results[c].score = Math.ceil(
      inverted.includes(c) ? 100 - value : value
    )

    if (!notIncluded.includes(c)) {
      score.total_results.total_score.points += score.total_results[c].points
      score.total_results.total_score.maxPoints +=
        score.total_results[c].maxPoints
      score.total_results.total_score.score! += score.total_results[c].score!
    }
  })
  // if (isMotor){
  score.total_results.total_score.score! /=
    categories.length - notIncluded.length
  score.total_results.total_score.score! *= coef
  score.total_results.total_score.score! = Math.ceil(
    score.total_results.total_score.score!
  )
  // }
  // else {
  //   score.total_results.total_score.score = Math.ceil((score.total_results.total_score.points / score.total_results.total_score.maxPoints ) * 100)
  // }
}

export const childForms = child => {
  const forms = [FormType.selfEsteem, FormType.social]
  const lockedDate = child.assessments
    ? child.currentAssessment
    : new Date().getTime()

  const age = getAge(child.birth_date, lockedDate)

  if (age >= 3 && age < 9) {
    forms.push(FormType.language)
  }

  if (age >= 3 && age < 8) {
    forms.push(FormType.fineMotor)
    forms.push(FormType.grossMotor)
  }

  return forms
}

export const getQuestionnairesCount = (patientDetail: any) => {
  let count = 0

  const assessment = getCurrentAssessment(patientDetail)

  if (!assessment) {
    return count
  }

  for (const key of childForms(patientDetail)) {
    const score = get(assessment, formTypeRefs[key]) as IScore
    if (get(score, ['total_results', 'total_score', 'score']) === undefined) {
      ++count
    }
  }
  return count
}

export default {
  formKeyByType,
  getFormByTypeAndChild,
  getScore,
  isFormPassed,
  getTempAnswers,
  childForms,
  getQuestionnairesCount
}



// WEBPACK FOOTER //
// ./src/services/questionnaires.ts